import AuthenticationService from "service/auth/AuthenticationService";
import EnvironmentService from "service/environment/EnvironmentService";
import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Appointments API. These are the same endpoints the bridge dashboard calls —
 * see bridge-frontend/src/modules/misc/modules/navigo-module/services/NavigoDashboardUtils.js.
 *
 * The server authorises `doctor_web` exactly like `bridge_web`
 * (api-appointment-service/helpers/helpers.go IsAuthorizedClient), so the list
 * comes back tenant-wide rather than scoped to the logged-in doctor. Scoping to
 * "my" appointments is done here, by resolving the doctor to a service provider
 * — see resolveMyServiceProviderId().
 */
class AppointmentService {
  constructor() {
    this.authenticationService = new AuthenticationService();
    this.environmentService = new EnvironmentService();
    this.apiService = new APIService();
  }

  /**
   * The appointments list. `filters` is sent as the whole request body; absent
   * keys mean "unfiltered", so callers delete keys rather than sending nulls.
   * Responds with { appointments, pageNum, pageSize, totalCount, resultCount }.
   */
  fetchAppointments(filters, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.fetchAppointments;

    return this.apiService.post(url, filters, onResponse, onError);
  }

  /**
   * THE status change — every status, the cancel family included.
   *
   * ⚠️ Sends NO refundRequest, BY DECISION (2026-09-14): refunds on phr are the
   * expert dashboard's, whose AppointmentDetails sends {appointmentID, status}
   * and nothing about money. Omitting refundRequest zero-values it to
   * UNKNOWN(0), and api-appointment-service (appointment_controller.go ~4134 on
   * zylahealth release 727042d) starts createAutoRefund for 0 on Cancelled /
   * No show / Declined / Not eligible — so the SERVER decides the refund: a paid
   * appointment is refunded in full, recorded as created by the doctor, unless
   * it is an in-person consultation or vaccine inside the cutoff. See
   * hooks/useStatusChange for the full consequence. Do not add refundRequest
   * here without asking.
   *
   * reportUrl is always "" — bridge's two paths both send empty. The server
   * writes reportURL unconditionally (repository/appointment.go:1718), so this
   * clears any stored report link; that is bridge's established behaviour.
   */
  updateStatus(appointmentID, status, onResponse, onError, reason) {
    const url = APIConfig.myPlan.navigo.statusUpdate;
    const data = { appointmentID, reportUrl: "", status };
    /* Only when there is one. The server $sets `updateReason` UNCONDITIONALLY
       (repository/appointment.go:2057), so sending "" would blank a stored
       reason — and bridge omits it on plain status changes, so adding it there
       would be a divergence, not a fix. */
    if (reason) data.reason = reason;

    return this.apiService.post(url, data, onResponse, onError);
  }


  /**
   * The cancellation-reason master list. Responds a bare array of strings.
   *
   * WHY IT MATTERS BEYOND THE UI: the server writes the reason to the
   * appointment (`updateReason`) and to the audit trail on every status change
   * — and it does so UNCONDITIONALLY, so a cancel that sends no reason does not
   * merely fail to record one, it BLANKS whatever was stored.
   */
  getCancellationReasons(onResponse, onError) {
    const url = APIConfig.myPlan.navigo.cancellationReasons;

    return this.apiService.get(url, onResponse, onError);
  }

  /** Reason id -> name. Fetch once per mount and map; never per row. */
  getReasonsForConsultation(onResponse, onError) {
    const url = APIConfig.myPlan.navigo.reasonsForConsultation;

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * One appointment, by id — the filter endpoint with a page of one. Used where a
   * screen is keyed on an appointment id (patient screen, Create Rx, health plan).
   */
  fetchAppointmentById(appointmentId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.fetchAppointments;
    const body = { pageNum: 1, pageSize: 1, appointmentId, statusOrderSort: true };

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * One appointment's FULL detail for the appointment-details screen — bridge's
   * DocumentUpload.fetchAppointment. Same filter endpoint, but asks for the
   * payment block too (includeAppointmentPayments), so paymentInfo/refundInfo
   * ride along on the row rather than needing a second call.
   */
  fetchAppointmentDetail(appointmentId, onResponse, onError) {
    // `includeEvents` is a QUERY PARAM, not a body key — the controller reads it
    // off the URL. Bridge sets it in the body and its util rewrites the URL
    // (NavigoDashboardUtils:106-108); phr appends it directly. It is what makes
    // `callData.events` ride along, so the Call history table has a source.
    // `&`, not `?`: this URL already carries includeDetails/includeServiceDetails.
    const url = APIConfig.myPlan.navigo.fetchAppointments + "&includeEvents=true";
    const body = {
      pageNum: 1,
      pageSize: 1,
      appointmentId,
      includeAppointmentPayments: true,
    };

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Documents mapped to one appointment — bridge's getAppointmentDocMapping.
   * Responds { documents: [...] }. Appointment-scoped (not the patient-wide list
   * behind the header's "View all").
   */
  getAppointmentDocMapping(appointmentId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getAppointmentDocMapping.format(appointmentId);

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * The appointment's audit trail — bridge's getAppointmentAudit. Backs the
   * Appointment history table AND the strip's reschedule count, which has no
   * other source: the filter endpoint returns no reschedule field at all.
   */
  getAppointmentAudit(appointmentId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getAppointmentAudit.format(appointmentId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** A signed, ~15-minute URL for one call recording. Responds { url }. */
  getCallRecordingSignedUrl(recordingId, onResponse, onError) {
    const url =
      APIConfig.myPlan.navigo.getCallRecordingSignedUrl.format(recordingId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** PUT, value in the path, null body — see the APIConfig note. */
  updateExtOrderId(appointmentId, externalOrderId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.updateExtOrderId.format(
      appointmentId,
      encodeURIComponent(externalOrderId)
    );

    return this.apiService.put(url, null, onResponse, onError);
  }

  /**
   * Upload one document — bridge's NavigoDashboardUtils.uploadDocument.
   *
   * FOUR multipart parts, and `document` is a JSON STRING rather than nested
   * fields: appending the object directly serialises it to "[object Object]"
   * and the server stores an empty record.
   */
  /**
   * "Update booking for" — PUT { memberPatientId }, which sets the appointment's
   * own `updatedBookingFor` field: the patient phr shows and acts on for it. The
   * appointment's patientID / bookedFor are never changed. Sending the member
   * the appointment is booked for switches it back.
   */
  setBookingFor(appointmentId, memberPatientId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.bookingFor.format(appointmentId);
    return this.apiService.put(url, { memberPatientId: Number(memberPatientId) }, onResponse, onError);
  }

  uploadDocument(file, fileName, document, onResponse, onError, patientId) {
    const form = new FormData();
    form.append("file", file);
    form.append("fileName", fileName);
    form.append(
      "document",
      JSON.stringify({
        patientId: patientId,
        title: fileName,
        description: document.description,
        type: document.type,
        format: document.format,
        status: document.status,
      })
    );
    form.append("patientId", patientId);

    return this.apiService.postWithFormData(
      APIConfig.documentApis.uploadDoc,
      form,
      onResponse,
      onError
    );
  }

  /** Attach an uploaded document to this appointment. { appointmentId, documentId } */
  mapDocToAppointment(body, onResponse, onError) {
    return this.apiService.post(
      APIConfig.myPlan.navigo.mapDocToApp,
      body,
      onResponse,
      onError
    );
  }

  /** Fire-and-forget lab-test digitisation hook. { patientId, documentURL } */
  labtestAutomation(body, onResponse, onError) {
    return this.apiService.post(
      APIConfig.myPlan.navigo.labtestAutomationWebhook,
      body,
      onResponse,
      onError
    );
  }

  getServiceTypes(onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getModes;

    return this.apiService.post(url, {}, onResponse, onError);
  }

  /**
   * Bookable slots for a date — the reschedule reuse of the create flow's call
   * (bridge's DocumentUpload.getAvailableSlots). Body is built by the caller
   * from the appointment record; it MUST carry isReschedule:true so the server
   * offers the reschedule window, and serviceProviderId must be the real id
   * (0 is bridge's auto-select and lets the server pick a different doctor).
   * Response: { slots: [{ key, value }], serviceProviderId }, value = minutes
   * since midnight. Read-only, so plain post() is fine.
   */
  getAvailableSlots(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getAvailableSlots;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Move the appointment to a new date + slot — bridge's navigo-module body
   * verbatim (DocumentUpload.rescheduleAppointment): { appointmentID,
   * appointmentDate: "YYYY-MM-DDT00:00:00.000Z", timeslot (minutes, note the
   * lowercase 's'), timeSlotId, serviceProviderID }.
   *
   * postAgain, NEVER post — same reason as createAppointment: post()'s error
   * path re-fires on ANY non-2xx (newAPIService.js), and a 500 thrown after the
   * row moved would reschedule twice / double-notify. postAgain is one attempt.
   */
  rescheduleAppointment(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.rescheduleAppointment;

    return this.apiService.postAgain(url, body, onResponse, onError);
  }

  /**
   * The reschedule dialog's reason chips — bridge's getRescheduleReasons. A BARE
   * ARRAY of strings, rendered verbatim and in order; "Other reason" is NOT part
   * of it and is appended by the dialog. Read-only.
   */
  getRescheduleReasons(onResponse, onError) {
    const url = APIConfig.myPlan.navigo.rescheduleReasons;

    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Slots for a whole date RANGE in one request — bridge's
   * getAvailableSlotsRange, used by its reschedule strip so every day can show
   * whether it has anything to offer before it is clicked. Body carries
   * startDate/endDate and isReschedule:true; response is
   * { dates: [{ date, slots: [{key, value, label?}], error, serviceProviderId }] }
   * and a date MISSING from `dates` must be treated as a failure for that date.
   * Read-only, so plain post() is fine.
   */
  getAvailableSlotsRange(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getAvailableSlotsRange;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * The Language options for a plan/service/mode — bridge's getLanguages:
   * { planId, serviceId, serviceMode } -> { languages: [display names] }.
   * Read-only.
   */
  getProviderLanguages(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getProviderLanguages;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * The providers a reschedule may move to — the SAME endpoint and body shape
   * bridge's reschedule uses (/v1/account/plans/service/provider/patient), with
   * `languageMatch` narrowing it. Rows with id 0 are excluded-provider
   * placeholders and must never be offered. Read-only.
   */
  getRescheduleProviders(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getServiceProviderForPatient;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /** Avail-care booking window — bridge's getAvailCareDates. Read-only. */
  getAvailCareDates(patientId, planId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getAvailCareDates
      .replace("{0}", patientId)
      .replace("{1}", planId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Home-sample booking window — bridge's getHomeSampleDates. Read-only. */
  getHomeSampleDates(patientId, planId, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getHomeSampleDates
      .replace("{0}", patientId)
      .replace("{1}", planId);

    return this.apiService.get(url, onResponse, onError);
  }

  /** Home-sample VACCINE booking window — bridge's getBookingConfigByVendor.
      A POST, but read-only. */
  getBookingConfigByVendor(body, onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getBookingConfigByVendor;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /** The priority vendors — bridge's getPriorityVendors. Read-only. */
  getPriorityVendors(onResponse, onError) {
    const url = APIConfig.myPlan.navigo.getPriorityVendors;

    return this.apiService.get(url, onResponse, onError);
  }

  getServices(body, onResponse, onError) {
    const url = APIConfig.myPlan.serviceDashboard.fetchServicesByFilter;

    return this.apiService.post(url, body, onResponse, onError);
  }

  getServiceProviders(body, onResponse, onError) {
    const url = APIConfig.myPlan.serviceProviderDashboard
      .fetchServiceProvidersByFilter;

    return this.apiService.post(url, body, onResponse, onError);
  }

  getAccounts(body, onResponse, onError) {
    const url = APIConfig.myPlan.accountDashboard.getAccountsByFilter;

    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Which service provider is the logged-in doctor?
   *
   * Neither the appointment filter nor the provider filter accepts a doctorId
   * (see AppointmentFilters / ServiceProviderFilters), so the mapping has to be
   * resolved client-side: a consultation provider carries the doctor it belongs
   * to on its consultation info.
   *
   * Wire shape (do not "simplify" these key names — two of three were wrong once,
   * which made this return null on every call and silently unscoped the list):
   *   res.providers[].consultationInfo.identityDetails.doctorId
   * `providers` per models/request_response.go:240 and every bridge call site;
   * `consultationInfo` per request_response.go:331 — bridge/expert-dashboard use
   * `consultationInfo` 700+ times and `consultationData` zero times.
   *
   * onResponse receives null when no provider matches. Callers MUST treat null
   * as a hard failure and refuse to show data, NOT as "don't scope" — the
   * unscoped list is every doctor's patients, which this screen must never show.
   *
   * doctorProfile.id and identityDetails.doctorId DO share an id space —
   * verified against dev1 2026-07-15: doctorProfile.id 845 resolved to provider
   * 220, and every appointment returned carried serviceProviderID 220.
   */
  resolveMyServiceProviderId(onResponse, onError) {
    return this.resolveMyServiceProviderIds(
      (ids) => onResponse(ids && ids.length ? ids[0] : null),
      onError
    );
  }

  /**
   * EVERY service provider the logged-in user owns, as an array of ids.
   *
   * One doctor can own several provider rows (different subtypes / clinics), so
   * `find()` was wrong: it silently pinned to whichever row came back first.
   *
   * `onResponse([])` means "this account has NO provider identity" — either it
   * carries no doctorProfile.id (a pure ops employee) or no provider row
   * references it. Callers enforcing the self-only rule MUST treat an empty
   * array as "cannot book", never as "unscoped": there is no employee→provider
   * mapping anywhere in the backend (ConsultationIdentityDetails carries
   * doctorId and nothing else), so an empty answer is genuinely "no self".
   *
   * Scoped to `serviceTypeIds:[1]` — consultation rows are the only ones that
   * carry a doctorId at all — and PAGED to exhaustion, because a provider
   * sitting past the first page used to resolve to "none" and lock the user out.
   */
  resolveMyServiceProviderIds(onResponse, onError) {
    const user = this.authenticationService.getUserProfile();
    const doctorId = Number(
      (user && user.doctorProfile && user.doctorProfile.id) || 0
    );

    if (!doctorId) {
      onResponse([]);
      return undefined;
    }

    const PAGE_SIZE = 500;
    const mine = [];

    const fetchPage = (pageNum) =>
      this.getServiceProviders(
        {
          pageNum,
          pageSize: PAGE_SIZE,
          status: "ACTIVE",
          serviceTypeIds: [1],
        },
        (res) => {
          const providers = (res && res.providers) || [];
          providers.forEach((p) => {
            const details =
              p && p.consultationInfo && p.consultationInfo.identityDetails;
            // Number() both sides: the wire is int64, the profile id is
            // whatever the login stored, and === between them silently missed.
            if (details && Number(details.doctorId) === doctorId && Number(p.id) > 0) {
              mine.push(Number(p.id));
            }
          });
          const total = Number((res && (res.totalCount || res.resultCount)) || 0);
          const seen = pageNum * PAGE_SIZE;
          if (providers.length === PAGE_SIZE && seen < total) {
            fetchPage(pageNum + 1);
            return;
          }
          onResponse(mine);
        },
        onError
      );

    return fetchPage(1);
  }
}

export default AppointmentService;
